import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProviderUserListSortCol, TProviderUserList, TProviderUserListResponse, TProviderUserListQuery, TProviderUserListSortCol, adaptProviderUserFromListResponse, adaptProviderUserListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_PROVIDER_USER } from '../../backend-paths';


export const providerUserListGetApi = createAsyncThunk(
	BACKEND_ADMIN_PROVIDER_USER,
	(payload: TProviderUserListQuery | undefined, thunkApi) => {
		const params = adaptProviderUserListQueryToRequest(payload);

		return HttpService.get<TProviderUserListResponse>(BACKEND_ADMIN_PROVIDER_USER, { params }).then(result => {
			return adaptProviderUserFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProviderUserList;
	query: TProviderUserListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProviderUserListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const providerUserListSlice = createSlice({
	name: 'providerUserListSlice',
	initialState,
	reducers: {
		setProviderUserListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProviderUserListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProviderUserListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProviderUserListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProviderUserListQuerySortCol(state, action: PayloadAction<TProviderUserListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProviderUserList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[providerUserListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[providerUserListGetApi.fulfilled.type]: (state, action: PayloadAction<TProviderUserList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[providerUserListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProviderUserListSelector = () => useAppSelector(state => state[providerUserListSlice.name]);

export const { clearProviderUserList, setProviderUserListQueryLimit, setProviderUserListQueryOffset, setProviderUserListQuerySort, setProviderUserListQuerySortCol, setProviderUserListQuerySearch } = providerUserListSlice.actions;
